import React, { useState, useEffect } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import useAdminInstitution from "Explore/Hooks/useAdminInstitution";

import ReportDetailHeader from "./Components/ReportDetailHeader/ReportDetailHeader";
import ReportDetailEmptyState from "./Components/ReportDetailEmptyState/ReportDetailEmptyState";
import MetricsSummaryCard from "./Components/MetricsSummaryCard/MetricsSummaryCard";
import ResearchStrengthCard from "Explore/Pages/Overview/Components/ResearchStrengthCard";
import TopRankingCard from "Explore/Pages/Overview/Components/TopRankingCard";
import TopFacultyCard from "Explore/Pages/Overview/Components/TopFacultyCard";
import FeaturedGrantsCard from "Explore/Pages/Overview/Components/FeaturedGrantsCard";
import TopSponsorsCard from "Explore/Pages/Overview/Components/TopSponsorsCard";
import VisualViewTest from "Explore/Pages/Analysis/Components/VisualViewTest/VisualViewTest";
import { SocialImpactCard, SdgDetailDrawer } from "Explore/Components/SocialImpact";
import useBenchmarkingSocialImpact from "./hooks/useBenchmarkingSocialImpact";
import { isInstitutionEntity } from "./utils/socialImpactUtils";

import PeerEntitySelector from "Explore/Pages/Peer/Components/EntitySelector/PeerEntitySelector";
import FiltersRow from "Explore/Pages/Analysis/Components/FiltersRow/FiltersRow";

import { getReportDetail } from "Explore/Services/Analysis";
import { getInstitutionDetailSummaryByID } from "Explore/Services/Institution";
import { getAuthorProfileSummaryByID } from "Explore/Services/Author";
import { getUnitSummaryById } from "Explore/Services/Unit";
import { transformReportDetailResponse } from "./utils/reportDetailTransformer";
import { DEFAULT_REPORT_FILTERS } from "Explore/Config/reportDetailConfig";
import { Skeleton } from "components/ui/skeleton";
import { Button } from "components/ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";

const EMPTY_ARRAY = [];

const LoadingSkeleton = () => (
  <div className="flex flex-col gap-6 w-full animate-pulse">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Skeleton className="h-28 rounded-xl" />
      <Skeleton className="h-28 rounded-xl" />
      <Skeleton className="h-28 rounded-xl" />
      <Skeleton className="h-28 rounded-xl" />
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Skeleton className="h-96 rounded-xl" />
      <Skeleton className="h-96 rounded-xl" />
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Skeleton className="h-80 rounded-xl" />
      <Skeleton className="h-80 rounded-xl" />
    </div>
    <Skeleton className="h-64 w-full rounded-xl" />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Skeleton className="h-72 rounded-xl" />
      <Skeleton className="h-72 rounded-xl" />
    </div>
  </div>
);

// ── Main Content ─────────────────────────────────────────────────────────────

const ReportDetailContent = ({
  data,
  selectedEntity,
  filters,
  onRetry,
  initialFacultyTab,
  initialResearchTab,
  socialImpactData,
  isLoadingSocialImpact,
  socialImpactError,
  onRetrySocialImpact,
  fetchSocialImpactDetail,
}) => {
  const [selectedSdg, setSelectedSdg] = useState(null);
  const currentEntity = data?.entity || selectedEntity;
  const isInstitution = isInstitutionEntity(currentEntity);
  const tableRows = React.useMemo(() => {
    if (!currentEntity?.id) return EMPTY_ARRAY;
    return [
      {
        ...currentEntity,
        id: currentEntity.id,
        name: currentEntity.name || selectedEntity?.name || "Institution",
        type: currentEntity.entity_type || currentEntity.type || selectedEntity?.type || "institution",
      },
    ];
  }, [currentEntity, selectedEntity]);

  if (!data) return null;

  const kpis = data.kpis;
  const researchStrength = data.research_strength || data.researchStrength;
  const topRanking = data.top_ranking || data.topRanking;
  const topFaculty = data.top_faculty || data.topFaculty;
  const topSponsors = data.top_sponsors || data.topSponsors;
  const featuredGrants = data.featured_grants || data.featuredGrants;
  const hiddenFields = data.hidden_fields || data.hiddenFields || {};
  const errors = data.errors || {};

  const yearRange = data.entity?.year_range || filters?.yearRange || "last_10_years";
  const sponsorsTitle =
    yearRange === "last_5_years"
      ? "Top Sponsors (Last 5 Years)"
      : yearRange === "last_10_years"
      ? "Top Sponsors (Last 10 Years)"
      : "Top Sponsors";

  return (
    <div className="flex flex-col gap-6 w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Section 1: KPIs */}
      <div id="section-kpis" className="w-full">
        <MetricsSummaryCard
          kpis={kpis}
          yearRange={yearRange}
          subjectArea={filters?.subjectArea}
          error={errors["kpis"] || errors["kpi"]}
          isHidden={hiddenFields["kpis"] || hiddenFields["kpi"]}
          onRetry={onRetry}
        />
      </div>

      {/* Section 2: Visual Benchmarking Grid (Trend & Growth Charts) */}
      <div id="section-visual-benchmarking" className="scroll-mt-6 w-full">
        <VisualViewTest
          pinnedEntities={EMPTY_ARRAY}
          tableRows={tableRows}
          tableRecords={EMPTY_ARRAY}
          isActive={true}
          isTableReady={tableRows.length > 0}
          filters={filters}
          entityType={tableRows[0]?.type || "institution"}
          gridMode={true}
          displayedGridCharts={['trend', 'growth']}
        />
      </div>

      {/* Section 3: Research Strength & Top Faculty Members side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch w-full">
        <div id="section-research-strength" className="scroll-mt-6 h-full flex flex-col">
          <ResearchStrengthCard
            data={researchStrength}
            showAll={true}
            initialTab={initialResearchTab}
            error={errors["research_strength"] || errors["rs"]}
            isHidden={hiddenFields["research_strength"] || hiddenFields["rs"]}
            onRetry={onRetry}
          />
        </div>
        <div id="section-top-faculty" className="scroll-mt-6 h-full flex flex-col">
          <TopFacultyCard
            data={topFaculty}
            showAll={true}
            initialTab={initialFacultyTab}
            error={errors["top_faculty"] || errors["tf"] || errors["faculty"]}
            isHidden={hiddenFields["top_faculty"] || hiddenFields["tf"] || hiddenFields["faculty"]}
            onRetry={onRetry}
          />
        </div>
      </div>

      {/* Section 4: Top Ranking Subjects */}
      <div id="section-top-ranking" className="scroll-mt-6 w-full">
        <TopRankingCard
          data={topRanking}
          showAll={true}
          error={errors["top_ranking"] || errors["tr"] || errors["ranking"]}
          isHidden={hiddenFields["top_ranking"] || hiddenFields["tr"] || hiddenFields["ranking"]}
          onRetry={onRetry}
        />
      </div>

      {/* Section 5: Funding & Sponsors */}
      <div id="section-funding-sponsors" className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch w-full">
        <div id="section-featured-grants" className="scroll-mt-6 h-full flex flex-col">
          <FeaturedGrantsCard
            data={featuredGrants}
            showAll={true}
            error={errors["featured_grants"] || errors["fg"] || errors["grants"]}
            isHidden={hiddenFields["featured_grants"] || hiddenFields["fg"] || hiddenFields["grants"]}
            onRetry={onRetry}
          />
        </div>
        <div id="section-top-sponsors" className="scroll-mt-6 h-full flex flex-col">
          <TopSponsorsCard
            data={topSponsors}
            title={sponsorsTitle}
            showAll={true}
            error={errors["top_sponsors"] || errors["ts"] || errors["sponsors"]}
            isHidden={hiddenFields["top_sponsors"] || hiddenFields["ts"] || hiddenFields["sponsors"]}
            onRetry={onRetry}
          />
        </div>
      </div>

      {/* Section 6: Social Impact (UN SDG) */}
      {isInstitution && (
        <div id="section-social-impact" className="scroll-mt-6 w-full">
          <SocialImpactCard
            data={socialImpactData}
            isLoading={isLoadingSocialImpact}
            error={socialImpactError}
            isHidden={hiddenFields["social_impact"] || hiddenFields["sdg"]}
            onRetry={onRetrySocialImpact}
            onSdgClick={setSelectedSdg}
          />
        </div>
      )}

      {/* SDG Detail Drawer */}
      <SdgDetailDrawer
        sdgNum={selectedSdg}
        isOpen={Boolean(selectedSdg)}
        onClose={() => setSelectedSdg(null)}
        fetchDetail={fetchSocialImpactDetail}
        rankLabel="Institution Rank"
        shareLabel="Share of Impact"
      />
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const ReportDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lastScrolledSectionRef = React.useRef(null);

  const urlEntityId = searchParams.get("entity_id") || location.state?.entityId;
  const urlEntityType = searchParams.get("entity_type") || location.state?.entityType || "institution";
  const urlYearRange = searchParams.get("year_range") || location.state?.filters?.yearRange || "last_10_years";
  const urlSubjectArea = searchParams.get("subject_area")
    ? searchParams.get("subject_area").split(",")
    : location.state?.filters?.subjectArea || [];

  const [selectedEntity, setSelectedEntity] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [filters, setFilters] = useState(
    location.state?.filters || {
      yearRange: urlYearRange,
      subjectArea: urlSubjectArea,
    }
  );

  const { institution: adminInstitution, isLoading: isAdminInstLoading } = useAdminInstitution();

  const currentEntityForSocialImpact = reportData?.entity || selectedEntity;
  const {
    data: socialImpactData,
    isLoading: isLoadingSocialImpact,
    error: socialImpactError,
    refetch: fetchSocialImpact,
    fetchDetail: fetchSocialImpactDetail,
  } = useBenchmarkingSocialImpact(currentEntityForSocialImpact, filters);

  useEffect(() => {
    if (!isAdminInstLoading && !hasInitialized) {
      const clearUrlParams = () => {
        if (Array.from(searchParams.keys()).length > 0) {
          navigate(location.pathname + (location.hash || ""), { replace: true, state: location.state });
        }
      };

      if (location.state?.entity) {
        const incoming = location.state.entity;
        const isNumeric = (val) => val != null && !isNaN(Number(val)) && String(val).trim() !== "";
        if (!isNumeric(incoming.id)) {
          const handleApiResult = (data) => {
            const canonicalId = data?.id || incoming.id;
            const fullEntity = { ...incoming, ...data, id: canonicalId, type: incoming.type || urlEntityType };
            handleEntitySelect(fullEntity);
            clearUrlParams();
            setHasInitialized(true);
          };
          if (incoming.type === "researcher") {
            getAuthorProfileSummaryByID(incoming.id, handleApiResult);
          } else if (incoming.type === "unit") {
            getUnitSummaryById(incoming.id).then(handleApiResult);
          } else {
            getInstitutionDetailSummaryByID(incoming.id, handleApiResult);
          }
        } else {
          handleEntitySelect(incoming);
          clearUrlParams();
          setHasInitialized(true);
        }
      } else if (urlEntityId) {
        const handleApiResult = (data) => {
          const canonicalId = data?.id || urlEntityId;
          const fullEntity = { ...data, id: canonicalId, type: urlEntityType };
          handleEntitySelect(fullEntity);
          clearUrlParams();
          setHasInitialized(true);
        };

        if (urlEntityType === "researcher") {
          getAuthorProfileSummaryByID(urlEntityId, handleApiResult);
        } else if (urlEntityType === "institution") {
          getInstitutionDetailSummaryByID(urlEntityId, handleApiResult);
        } else if (urlEntityType === "unit") {
          getUnitSummaryById(urlEntityId).then(handleApiResult);
        } else {
          clearUrlParams();
          setHasInitialized(true);
        }
      } else if (adminInstitution) {
        handleEntitySelect(adminInstitution);
        clearUrlParams();
        setHasInitialized(true);
      } else {
        clearUrlParams();
        setHasInitialized(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminInstitution, isAdminInstLoading, hasInitialized]);

  // Smooth scroll to target section when report data finishes loading
  useEffect(() => {
    const rawTarget =
      location.state?.targetSection ||
      location.state?.section ||
      searchParams.get("section") ||
      (location.hash ? location.hash.replace(/^#/, "") : null);

    if (!rawTarget || isLoading || !reportData) return;

    if (lastScrolledSectionRef.current === rawTarget) return;

    const timer = setTimeout(() => {
      const targetId = rawTarget.startsWith("section-") ? rawTarget : `section-${rawTarget}`;
      const el =
        document.getElementById(targetId) ||
        document.getElementById(rawTarget) ||
        (["trend", "section-trend", "charts", "section-charts"].includes(rawTarget)
          ? document.getElementById("section-visual-benchmarking")
          : ["sdg", "section-sdg", "social-impact", "section-social-impact"].includes(rawTarget)
          ? document.getElementById("section-social-impact")
          : null);
      if (el) {
        lastScrolledSectionRef.current = rawTarget;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("ring-2", "ring-explore", "ring-offset-4", "rounded-xl", "transition-all", "duration-500");
        const removeTimer = setTimeout(() => {
          el.classList.remove("ring-2", "ring-explore", "ring-offset-4");
        }, 2200);

        return () => clearTimeout(removeTimer);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [isLoading, reportData, location.state, location.hash, searchParams]);

  const fetchReport = async (entity, currentFilters) => {
    if (!entity?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const entityType = entity.type || "institution";
      const subjectArea = Array.isArray(currentFilters?.subjectArea)
        ? currentFilters.subjectArea.slice(0, 1)
        : currentFilters?.subjectArea
        ? [currentFilters.subjectArea]
        : [];

      const rawReport = await getReportDetail({
        entityType,
        entityId: entity.id,
        yearRange: currentFilters?.yearRange || "last_10_years",
        subjectArea,
      });

      if (!rawReport) {
        throw new Error("No data returned from report service");
      }

      setReportData(transformReportDetailResponse(rawReport));
    } catch (err) {
      console.error("Failed to fetch report detail:", err);
      setError(err?.response?.data?.message || err?.message || "Failed to load report. Please try again.");
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEntitySelect = (entity) => {
    setSelectedEntity(entity);
    fetchReport(entity, filters);
  };

  const handleEntityClear = () => {
    setSelectedEntity(null);
    setReportData(null);
    setError(null);
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/50 w-full">
      <div className="flex flex-col flex-1 p-6 gap-6 w-full">
        {/* Page Header */}
        <ReportDetailHeader entity={reportData?.entity || selectedEntity} />

        {/* Entity Selector */}
        <PeerEntitySelector
          selectedEntity={selectedEntity}
          onSelect={handleEntitySelect}
          onClear={handleEntityClear}
        />

        {/* Content Area */}
        {!hasInitialized ? (
          <div className="flex w-full mt-6">
            <LoadingSkeleton />
          </div>
        ) : !selectedEntity ? (
          <ReportDetailEmptyState />
        ) : (
          <div className="flex flex-col lg:flex-row items-start flex-1 gap-6 w-full">
            {/* Left Sidebar: Filters */}
            <div className="w-full lg:w-[280px] flex-shrink-0 lg:sticky lg:top-0 h-max max-h-screen overflow-y-auto bg-white border border-slate-200 shadow-sm rounded-lg">
              <FiltersRow
                filters={filters}
                onFilterChange={setFilters}
                entityType={selectedEntity?.type || "institution"}
                onReset={() => {
                  setFilters(DEFAULT_REPORT_FILTERS);
                  if (selectedEntity) {
                    fetchReport(selectedEntity, DEFAULT_REPORT_FILTERS);
                  }
                }}
                onRunAnalysis={(newFilters) => {
                  setFilters(newFilters);
                  if (selectedEntity) {
                    fetchReport(selectedEntity, newFilters);
                  }
                }}
              />
            </div>

            {/* Right side content */}
            <div className="flex-1 min-w-0 flex flex-col gap-8 w-full">
              {isLoading ? (
                <LoadingSkeleton />
              ) : error ? (
                <div className="flex flex-col items-center justify-center p-12 bg-white border border-destructive/20 rounded-xl text-center gap-3">
                  <AlertCircle className="w-10 h-10 text-destructive mb-1" />
                  <p className="text-destructive font-medium m-0">{error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchReport(selectedEntity, filters)}
                    className="gap-2 mt-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Retry
                  </Button>
                </div>
              ) : (
                <ReportDetailContent
                  data={reportData}
                  selectedEntity={selectedEntity}
                  filters={filters}
                  initialFacultyTab={location.state?.tab}
                  initialResearchTab={location.state?.researchTab}
                  onRetry={() => {
                    fetchReport(selectedEntity, filters);
                    fetchSocialImpact();
                  }}
                  socialImpactData={socialImpactData}
                  isLoadingSocialImpact={isLoadingSocialImpact}
                  socialImpactError={socialImpactError}
                  onRetrySocialImpact={fetchSocialImpact}
                  fetchSocialImpactDetail={fetchSocialImpactDetail}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportDetail;
